import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map, switchMap, of, catchError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { AffiliateService } from '../services/affiliate.service';

/** Helper: send user to their own dashboard based on role. */
function redirectToDashboard(auth: AuthService, router: Router): void {
  if (auth.isAdmin()) {
    router.navigate(['/app/admin/dashboard']);
  } else if (auth.isVendor()) {
    router.navigate(['/app/vendor/dashboard']);
  } else {
    router.navigate(['/app/search']);
  }
}

/** Prevents unauthenticated users from accessing protected routes. */
export const authGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  return auth.ensureSessionSynced().pipe(
    map(() => {
      if (auth.isAuthenticated()) {
        return true;
      }

      router.navigate(['/auth/login']);
      return false;
    })
  );
};

/** Prevents authenticated users from seeing auth pages (login/register). */
export const guestGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  return auth.ensureSessionSynced().pipe(
    map(() => {
      if (!auth.isAuthenticated()) {
        return true;
      }

      redirectToDashboard(auth, router);
      return false;
    })
  );
};

/** Restricts to admin users only. */
export const adminGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  return auth.ensureSessionSynced().pipe(
    map(() => {
      if (auth.isAuthenticated() && auth.isAdmin()) {
        return true;
      }

      if (!auth.isAuthenticated()) {
        router.navigate(['/auth/login']);
      } else {
        redirectToDashboard(auth, router);
      }
      return false;
    })
  );
};

/** Restricts to vendor users only. */
export const vendorGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  return auth.ensureSessionSynced().pipe(
    map(() => {
      if (auth.isAuthenticated() && auth.isVendor()) {
        return true;
      }

      if (!auth.isAuthenticated()) {
        router.navigate(['/auth/login']);
      } else {
        redirectToDashboard(auth, router);
      }
      return false;
    })
  );
};

/** Restricts to non-admin users only (e.g. CAMs and Vendors). */
export const affiliateGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  return auth.ensureSessionSynced().pipe(
    map(() => {
      if (auth.isAuthenticated() && !auth.isAdmin()) {
        return true;
      }

      if (!auth.isAuthenticated()) {
        router.navigate(['/auth/login']);
      } else {
        redirectToDashboard(auth, router);
      }
      return false;
    })
  );
};

/**
 * Hard gate on the affiliate area: blocks any affiliate route (other than the tax-profile-setup
 * page itself) until the affiliate has a certified tax profile on file, per 1099 compliance
 * requirements. Applied alongside affiliateGuard on the routes it guards.
 */
export const taxProfileGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const affiliateService = inject(AffiliateService);
  const router = inject(Router);

  return auth.ensureSessionSynced().pipe(
    switchMap(() => {
      if (!auth.isAuthenticated()) {
        router.navigate(['/auth/login']);
        return of(false);
      }

      return affiliateService.getPayoutOnboardingStatus().pipe(
        map((status) => {
          if (status.taxProfileComplete) {
            return true;
          }
          router.navigate(['/app/affiliate/tax-profile-setup']);
          return false;
        }),
        // Not yet an affiliate (404) or a transient error — let the affiliate area's own
        // pages handle that; don't block navigation on this gate.
        catchError(() => of(true))
      );
    })
  );
};

/** Restricts to non-admin users only (e.g., CAMs and Vendors). */
export const nonAdminGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  return auth.ensureSessionSynced().pipe(
    map(() => {
      if (auth.isAuthenticated() && !auth.isAdmin()) {
        return true;
      }

      if (!auth.isAuthenticated()) {
        router.navigate(['/auth/login']);
      } else {
        redirectToDashboard(auth, router);
      }
      return false;
    })
  );
};
